// src/screens/EditMatch.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { LogoLoader } from "../components/LogoLoader";
import { Home, Bus, Trophy, XCircle, Save, ArrowLeft } from "lucide-react";
import i18n from "../i18n";

function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      String((i18n.t as any)(key, options)),
    [] // ✅ pas de dépendances -> plus de warning
  );
  return { t, lng };
}


type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

type Match = {
  id: string;
  user_id?: string;
  opponent: string;
  date: string;
  players: Player[] | null;
  score?: string;
  location?: "domicile" | "extérieur";
  victory?: boolean;
};

export const EditMatch = () => {
  const { t } = useI18nSafe();
  const { id: matchId } = useParams();
  const navigate = useNavigate();

  const [match, setMatch] = useState<Match | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [score, setScore] = useState("");
  const [location, setLocation] = useState<"domicile" | "extérieur">("domicile");
  const [victory, setVictory] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const opponentLabel = useMemo(() => match?.opponent ?? "", [match]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) console.error(userError);
        if (!user) {
          navigate("/login");
          return;
        }

        const { data: matchData, error: matchError } = await supabase
          .from("matches")
          .select("*")
          .eq("id", matchId)
          .eq("user_id", user.id)
          .single();

        if (matchError || !matchData) {
          console.error("Load error:", matchError);
          alert("Erreur de chargement du match.");
          navigate("/allmatchs");
          return;
        }

        if (!isMounted) return;
        setMatch(matchData as Match);
        setSelectedPlayers(Array.isArray(matchData.players) ? (matchData.players as Player[]) : []);
        setScore(matchData.score ?? "");
        setLocation((matchData.location as "domicile" | "extérieur") ?? "domicile");
        setVictory(Boolean(matchData.victory));
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // ❗ Ne surtout pas dépendre de `t` ici (sinon refetch infini)
  }, [matchId, navigate]);

  const handleSubmit = async () => {
    if (!matchId) return;
    setSaving(true);
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) console.error(userError);
      if (!user) {
        navigate("/login");
        return;
      }

      const { data, error } = await supabase
        .from("matches")
        .update({
          players: selectedPlayers,
          score: score.trim(),
          location,
          victory,
        })
        .eq("id", matchId)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error || !data) {
        console.error("Erreur mise à jour :", error);
        alert(t("editmatch:alerts.update_error"));
        return;
      }

      alert(t("editmatch:alerts.update_ok"));
      navigate(`/matchdetails/${matchId}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !match) {
    return (
      <LogoLoader />
    );
  }

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link
            to={`/matchdetails/${matchId}`}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common:back")}
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-center mb-6 text-orange-600 dark:text-orange-400">
          {t("editmatch:title", { opponent: opponentLabel })}
        </h1>

        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          {/* Score */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editmatch:score.label")}
            </label>
            <input
              type="text"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              placeholder={t("editmatch:score.placeholder")}
              className="w-full rounded-lg px-3 py-2 border
                         bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
            />
          </div>

          {/* Lieu */}
          <div className="mb-6">
            <p className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-200">
              {t("editmatch:location.title")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setLocation("domicile")}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition
                  ${
                    location === "domicile"
                      ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  }`}
                aria-pressed={location === "domicile"}
              >
                <Home className="w-4 h-4" />
                {t("editmatch:location.home")}
              </button>
              <button
                type="button"
                onClick={() => setLocation("extérieur")}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition
                  ${
                    location === "extérieur"
                      ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  }`}
                aria-pressed={location === "extérieur"}
              >
                <Bus className="w-4 h-4" />
                {t("editmatch:location.away")}
              </button>
            </div>
          </div>

          {/* Résultat */}
          <div className="mb-6">
            <p className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-200">
              {t("editmatch:result.title")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setVictory(true)}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition
                  ${
                    victory === true
                      ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  }`}
                aria-pressed={victory === true}
              >
                <Trophy className="w-4 h-4" />
                {t("editmatch:result.win")}
              </button>
              <button
                type="button"
                onClick={() => setVictory(false)}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition
                  ${
                    victory === false
                      ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  }`}
                aria-pressed={victory === false}
              >
                <XCircle className="w-4 h-4" />
                {t("editmatch:result.loss")}
              </button>
            </div>
          </div>

          {/* Sauvegarde */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-lg transition
                        focus:outline-none focus:ring-2 focus:ring-orange-400/60
                        ${
                          saving
                            ? "bg-orange-300 text-white cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700 text-white"
                        }`}
            aria-busy={saving}
          >
            <Save className="w-4 h-4" />
            {saving ? t("editmatch:buttons.saving") : t("editmatch:buttons.save")}
          </button>
        </div>
      </div>
    </section>
  );
};
